import type { ExtractAtomValue } from 'jotai';
import { t } from '@/features/i18n/utils';
import type { Citation } from '@/features/documents/types';
import type { ContractRedline, DeviationSource } from '@/features/standard-deviations/types';
import { diffWords, type DiffToken } from '@/ui/components/editor/proposal-preview';
import { failsModeFilter } from '@/filters';
import type {
  contractReviewDomainFilterAtom,
  contractReviewFilterAtom,
} from '@/features/contract-review/store';
import {
  CONTRACT_REVIEW_SEVERITIES,
  getClusterSeverity,
  getDeviationSeverityFromScore,
} from '@/features/contract-review/constants';
import {
  getUserReviewSortValue,
  isIgnoredUserReview,
  normalizeUserReview,
} from '@/features/user-review/utils';

export const humanizeDomainName = (name: string) => {
  const spaced = name.replaceAll('_', ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const filterDomains = <
  Domain extends {
    domain: string;
    name: string;
    domainNarrative: string | null;
    riskScore: number;
    clusters: { clusterName: string | null }[];
  },
>(
  domains: Domain[],
  filter: ExtractAtomValue<typeof contractReviewFilterAtom>,
  search: string,
) => {
  const query = search.trim().toLowerCase();
  const filtered = domains.filter(
    (domain) =>
      !failsModeFilter(
        filter.priority,
        getDeviationSeverityFromScore(domain.riskScore),
        filter.priorityMode,
      ) &&
      (!query ||
        [
          domain.name,
          domain.domain,
          domain.domainNarrative,
          ...domain.clusters.map((cluster) => cluster.clusterName),
        ].some((field) => field?.toLowerCase().includes(query))),
  );
  return filter.order?.direction === 'asc' ? filtered.toReversed() : filtered;
};

export const filterDomainClusters = <
  Cluster extends {
    id: string;
    clusterName: string | null;
    clusterSummary: string | null;
    consolidatedRiskDescription: string | null;
    representative: { statement: string };
    userReview: string | null;
    evidenceRedFlag: string | null;
    evidenceNeedsReview: string | null;
  },
>(
  clusters: Cluster[],
  filter: ExtractAtomValue<typeof contractReviewDomainFilterAtom>,
  { search = '', clusterId }: { search?: string; clusterId?: string },
) => {
  const query = search.trim().toLowerCase();
  const direction = filter.order?.direction === 'asc' ? -1 : 1;
  return clusters
    .filter(
      (cluster) =>
        cluster.id === clusterId ||
        ((filter.showNotRelevant || !isIgnoredUserReview(cluster.userReview)) &&
          !failsModeFilter(filter.severity, getClusterSeverity(cluster), filter.severityMode) &&
          !failsModeFilter(
            filter.userReview,
            normalizeUserReview(cluster.userReview),
            filter.userReviewMode,
          ) &&
          (!query ||
            [
              cluster.clusterName,
              cluster.representative.statement,
              cluster.clusterSummary,
              cluster.consolidatedRiskDescription,
            ].some((field) => field?.toLowerCase().includes(query)))),
    )
    .sort(
      (a, b) =>
        (filter.order?.field === 'status'
          ? getUserReviewSortValue(b.userReview) - getUserReviewSortValue(a.userReview)
          : CONTRACT_REVIEW_SEVERITIES.indexOf(getClusterSeverity(a)) -
            CONTRACT_REVIEW_SEVERITIES.indexOf(getClusterSeverity(b))) * direction ||
        a.id.localeCompare(b.id),
    );
};

export const mapSourcesToCitations = (sources: DeviationSource[]): Citation[] => {
  const maxNum = Math.max(...sources.map((s) => parseInt(s.refKey.replace('doc', ''), 10)));
  const citations: Citation[] = new Array(maxNum);

  for (const source of sources) {
    const num = parseInt(source.refKey.replace('doc', ''), 10);
    citations[num - 1] = {
      chunkId: source.chunkIndex?.toString() ?? '',
      content: source.sourceText,
      title: source.docName,
      url: source.docId,
      filepath: source.docId,
      postfix: 0,
      order: num,
      pageStart: source.pageNumber ?? undefined,
    };
  }

  return citations;
};

export const formatRefLabel = (refKey: string) => {
  const deviation = /^dev(\d+)$/.exec(refKey)?.[1];
  if (deviation) return t({ en: `Deviation #${deviation}`, nb: `Avvik #${deviation}` });
  const clause = /^clause(\d+)$/.exec(refKey)?.[1];
  if (clause) return t({ en: `Clause #${clause}`, nb: `Klausul #${clause}` });
  return refKey;
};

const MAX_DIFF_WORDS = 400;
const PARAGRAPH_MARK = '\u0000';

const countWords = (text: string) => text.split(/\s+/).filter(Boolean).length;

const markParagraphs = (text: string) =>
  text
    .split(/\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .join(` ${PARAGRAPH_MARK} `);

const splitParagraphs = (tokens: DiffToken[]) => {
  const paragraphs: DiffToken[][] = [[]];
  for (const token of tokens) {
    token.text.split(PARAGRAPH_MARK).forEach((part, index) => {
      if (index > 0) paragraphs.push([]);
      const text = part.trim();
      if (text) paragraphs.at(-1)?.push({ kind: token.kind, text });
    });
  }
  return paragraphs.filter((paragraph) => paragraph.length);
};

export const redlineTokens = (amendment: ContractRedline['amendments'][number]): DiffToken[][] => {
  const original = amendment.source.source_text ?? '';
  const proposed = amendment.proposed_text;
  if (amendment.operation === 'delete') return [[{ kind: 'del', text: original }]];
  if (amendment.operation === 'insert_before')
    return [[{ kind: 'ins', text: proposed }], [{ kind: 'same', text: original }]];
  if (amendment.operation === 'insert_after')
    return [[{ kind: 'same', text: original }], [{ kind: 'ins', text: proposed }]];
  if (countWords(original) > MAX_DIFF_WORDS || countWords(proposed) > MAX_DIFF_WORDS)
    return [[{ kind: 'del', text: original }], [{ kind: 'ins', text: proposed }]];

  return splitParagraphs(diffWords(markParagraphs(original), markParagraphs(proposed)));
};
